import i18next from "i18next";
import type { DateTime } from "luxon";
import type { backendService } from "../../../../services/backend.service";
import { type AutoApproveRule, ExternalSource } from "../../../../utils/models";
import { isSameScheduleVisibilityDate, transformJobShiftTimesForBackend } from "../utils";
import type { GeneralFormData } from "./types";

export class AutoApproveRuleValidationError extends Error {}

const SHIFT_WINDOW_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// Validate time windows and normalize optional labels for the backend.
// undefined = no rules key on the form → leave the column untouched.
export function transformAutoApproveRulesForBackend(rules?: AutoApproveRule[]): AutoApproveRule[] | undefined {
	if (!rules) return undefined;
	for (const [index, rule] of rules.entries()) {
		// DEFAULT: an unnamed rule uses its visible position to identify invalid conditions.
		const reference = rule.name.trim() === "" ? i18next.t("department.config.staffApp.autoApprove.list.untitled", { number: index + 1 }) : rule.name.trim();
		for (const window of rule.shiftHours) {
			if (!SHIFT_WINDOW_TIME_PATTERN.test(window.start) || !SHIFT_WINDOW_TIME_PATTERN.test(window.end)) {
				throw new AutoApproveRuleValidationError(i18next.t("department.config.staffApp.autoApprove.validation.windowTime", { name: reference }));
			}
			if (window.start === window.end) {
				throw new AutoApproveRuleValidationError(i18next.t("department.config.staffApp.autoApprove.validation.windowEqual", { name: reference }));
			}
		}
	}
	return rules.map((rule) => ({
		...rule,
		name: rule.name.trim(),
		jobIds: rule.jobIds ?? [],
		shiftDays: rule.shiftDays ?? [],
		shiftHours: rule.shiftHours ?? [],
		maxOt: rule.maxOt ?? null,
		timeBeforeShift: rule.timeBeforeShift ?? null,
		staffingShortage: rule.staffingShortage ?? null,
	}));
}

/** The `config` body of `PUT /departments/{id}/config`, exactly as the Settings save sends it. */
export type DeptConfigUpdatePayload = Parameters<typeof backendService.updateGeneralForm>[1]["config"];

type DeptConfigUpdateContext = {
	/** The horizon the rest of the app is showing, to decide whether this save may move it. */
	publishedScheduleVisibilityDate: DateTime | undefined;
	jobManagementEnabled: boolean;
	/** Jobs with a persisted shift-times row, so an explicitly-cleared row is still sent. */
	persistedJobShiftTimeIds: Set<string>;
};

/**
 * Builds the Settings save's `/config` body from the form.
 *
 * DAT-905: `hoursRateConfig`, `overtimeConfig` and `breakRulesConfig` are destructured OUT, not
 * merely left unset. They are edited on Team → Wage Settings now, and the form still holds the copy
 * it loaded with the page; spreading the rest of the form would send that stale copy back. The
 * backend ignores the three keys too, but not sending them keeps the client honest on its own.
 */
export function buildDeptConfigUpdatePayload(generalFormData: GeneralFormData, context: DeptConfigUpdateContext): DeptConfigUpdatePayload {
	// jobShiftTimes is form-shaped (a Record keyed by job id); it's re-added below in the array shape
	// the backend expects, so keep it out of the spread. jobPpdOverrides is not in the config payload
	// at all: it flushes to its own endpoint once this PUT has succeeded.
	const {
		emrIntegrations,
		hrisIntegrations,
		staffScheduleDisplayUntilDate,
		jobShiftTimes: _jobShiftTimesForm,
		jobPpdOverrides: _jobPpdOverridesForm,
		facility: _facilityChosenOnCreateOnly,
		hoursRateConfig: _hoursRateConfigOwnedByTeam,
		overtimeConfig: _overtimeConfigOwnedByTeam,
		breakRulesConfig: _breakRulesConfigOwnedByTeam,
		...configData
	} = generalFormData;
	// The Staffing "Staff Schedule Visibility" popup and the auto-post job also own this date. Sending
	// it on every config save (from any tab) would roll their publish back, so the key is only
	// included when Ops actually changed it here.
	const scheduleVisibilityChanged = !isSameScheduleVisibilityDate(staffScheduleDisplayUntilDate, context.publishedScheduleVisibilityDate);

	return {
		...configData,
		// Only the department's own rows. The building's feeds are saved on Facility configuration, and the
		// department save refuses them (`department.config.hris.feed.belongs.to.facility`); a Clipboard row
		// left out is removed, which would silently turn the actuals switch off.
		hrisIntegrations: hrisIntegrations.filter((integration) => integration.source === ExternalSource.Clipboard),
		emrIntegrations,
		...(scheduleVisibilityChanged
			? { staffScheduleDisplayUntilDate: staffScheduleDisplayUntilDate ? staffScheduleDisplayUntilDate.toFormat("yyyy-MM-dd") : null }
			: {}),
		week1FirstSunday: generalFormData.week1FirstSunday.toFormat("yyyy-MM-dd"),
		autoApproveRules: transformAutoApproveRulesForBackend(generalFormData.autoApproveRules),
		jobShiftTimes: context.jobManagementEnabled
			? transformJobShiftTimesForBackend(generalFormData.jobShiftTimes, generalFormData.jobIds, context.persistedJobShiftTimeIds)
			: undefined,
	};
}
